import React, { useState } from "react";
import { Spinner } from "flowbite-react";
import { useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js";
import { confirmPaymentToApi, createPaymentIntent } from "../../services/callAPI/toPayment";
import { CguModal } from "../modales/Cgu/CguModal";

interface PaymentFormProps {
  onSuccess: () => void;
  onFailure: () => void;
  onError: () => void;
}

function PaymentForm({ onSuccess, onFailure, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState("");
  const [inProcessPayment, setinProcessPayment] = useState<boolean>(false);
  const [waiverRightChecked, setWaiverRightChecked] = useState(false);
  const [cguCgvChecked, setCguCgvChecked] = useState(false);
  // const [isCardComplete, setIsCardComplete] = useState(false);


  const handleWaiverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setWaiverRightChecked(e.target.checked);
  };

  const handleCguCgvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCguCgvChecked(e.target.checked);
  };

  async function handleSubmit(event: React.FormEvent) {
    try {
      event.preventDefault();
      setinProcessPayment(true); //indicate in process payment

      // Vérifier si l'utilisateur a accepté les conditions nécessaires
      if (!waiverRightChecked || !cguCgvChecked) {
        setErrorMessage("Vous devez accepter de renoncer à votre droit de rétractation et accepter les CGU/CGV.");
        setinProcessPayment(false);
        return;
      }

      //check stripe and element of stripe loaded
      if (!stripe || !elements) {
        console.log("stripe pas encore chargé!");
        setErrorMessage("Stripe n'est pas encore chargé. Veuillez réessayer.");// Stripe n'est pas encore chargé
        setinProcessPayment(false);
        return;
      }

      // api call to create PaymentIntent
      let client_secret;
      try {
        const { clientSecret } = await createPaymentIntent({ waiverRight: waiverRightChecked, cguCgv: cguCgvChecked });
        client_secret = clientSecret;
      } catch (error) {
        setErrorMessage("Impossible de créer l'intention de paiement. Veuillez réessayer plus tard.");
        setinProcessPayment(false);
        return;
      }

      // Confirmer le paiement avec Stripe
      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardNumberElement)!,
        }
      });
      // console.log("result de stripe: ", result);

      if (result.error) {
        setErrorMessage(result.error.message || "Une erreur est survenue lors du paiement.");
      }
      else if (result.paymentIntent && result.paymentIntent.status === 'succeeded') {
        const { message } = await confirmPaymentToApi(result.paymentIntent.id);

        if (message != "Paiement confirmé avec succès.") throw new Error("Aucun message de confirmation reçu du serveur.");

        onSuccess();
      }
      else {
        onError();
      }
    } catch (error) {
      onFailure();
    } finally {
      setinProcessPayment(false);
    }
  }

  return (
    <div className="w-full mx-auto p-4">

      {inProcessPayment && <Spinner color="warning" size="lg" aria-label="in process payment" />}

      <form onSubmit={handleSubmit} className="space-y-5 w-full max-w-sm mx-auto bg-white p-6 rounded-xl shadow-md">
        <h3 className="text-xl font-bold text-[#335C67] text-center">Paiement sécurisé</h3>
        <p className="text-sm text-gray-600 text-center">Entrez vos informations pour finaliser votre abonnement.</p>

        <div className="flex flex-col items-center space-y-4">
          <div className="w-11/12 max-w-xs">
            <label className="block text-sm font-medium text-[#335C67] mb-1">Numéro de carte</label>
            <div className="border border-[#E09F3E] rounded-lg p-2 focus-within:ring-2 focus-within:ring-[#E09F3E]">
              <CardNumberElement className="w-full" />
            </div>
          </div>

          <div className="w-11/12 max-w-xs">
            <label className="block text-sm font-medium text-[#335C67] mb-1">Date d'expiration</label>
            <div className="border border-[#E09F3E] rounded-lg p-2 focus-within:ring-2 focus-within:ring-[#E09F3E]">
              <CardExpiryElement className="w-full" />
            </div>
          </div>

          <div className="w-11/12 max-w-xs">
            <label className="block text-sm font-medium text-[#335C67] mb-1">Code de sécurité</label>
            <div className="border border-[#E09F3E] rounded-lg p-2 focus-within:ring-2 focus-within:ring-[#E09F3E]">
              <CardCvcElement className="w-full" />
            </div>
          </div>

          {/* bloc renonce droit de rétraction */}
          <div className="flex gap-3 items-center">
            <input id="waiverRight" name="waiverRight" type="checkbox" checked={waiverRightChecked} onChange={handleWaiverChange} />
            <label htmlFor="waiverRight" className="text-sm">Je reconnais et accepte renoncer à mon droit de rétractation </label>
          </div>

          {/* bloc accept cgu/cgv */}
          <div>
            <div className="flex gap-3 items-center">
              <input id="cgu-cgv" name="cguCgv" type="checkbox" checked={cguCgvChecked} onChange={handleCguCgvChange} />
              <label htmlFor="cguCgv" className="text-sm">Je reconnais avoir lu et j'accepte les</label>
            </div>
            <p className="mt-0 flex justify-center" ><CguModal /></p>
          </div>

        </div>

        <button
          style={{
            backgroundColor: "#E09F3E"
          }}
          type="submit"
          className="w-full bg-[#E09F3E] hover:bg-[#d68b28] text-white py-2 rounded-lg transition disabled:opacity-50"
          disabled={!stripe || inProcessPayment}
        >
          {inProcessPayment ? "Paiement en cours..." : "Payer"}
        </button>

        {errorMessage && <p className="text-[#540B0E] text-sm mt-2 text-center">{errorMessage}</p>}
      </form>

    </div>
  );
}

export default PaymentForm;